import isNil from "lodash/isNil";
import { ClaraStreamHttpError } from "../../../features/ai-chatbot/types/clara-stream-http.error";
import { ClaraStreamInterruptedError } from "../../../features/ai-chatbot/types/clara-stream-interrupted.error";
import { getAxiosPyrgosConfig } from "../../../legacy/common/pyrgos";
import { AiChatbotChunkDTO } from "../../../legacy/services/server-side-events-ai-chatbot/dtos/ai-chatbot-chunk.dto";
import { AiChatbotChunkEntity } from "../../../legacy/services/server-side-events-ai-chatbot/entities/ai-chatbot-chunk.entity";
import { Mapper } from "../../../lib/utils/mapper";
import { parseSseBuffer } from "./parse-sse-buffer";

const CLARA_STREAM_ENDPOINT = "/assistants/ask-within-session/stream-v2";
const DONE_EVENT = "done";
const ERROR_EVENT = "error";

export interface IStreamClaraAnswerParams {
  question: string;
  onChunk(chunk: AiChatbotChunkEntity): void;
}

function toChunkEntity(frameJson: string): AiChatbotChunkEntity {
  const dto = Mapper.mapData(AiChatbotChunkDTO, JSON.parse(frameJson));
  const entity = Mapper.mapData(AiChatbotChunkEntity, dto);
  return entity;
}

async function readUntilDone(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onChunk: (chunk: AiChatbotChunkEntity) => void
): Promise<boolean> {
  const decoder = new TextDecoder();
  let pendingText = "";
  let readResult = await reader.read();

  while (!readResult.done) {
    const parsed = parseSseBuffer(pendingText + decoder.decode(readResult.value, { stream: true }));
    pendingText = parsed.rest;

    for (const sseEvent of parsed.events) {
      const isDone = sseEvent.event === DONE_EVENT;
      if (isDone) {
        return true;
      }

      const isError = sseEvent.event === ERROR_EVENT;
      if (isError) {
        throw new ClaraStreamInterruptedError(sseEvent.data);
      }

      onChunk(toChunkEntity(sseEvent.data));
    }

    readResult = await reader.read();
  }

  return false;
}

export async function streamClaraAnswer(params: Readonly<IStreamClaraAnswerParams>): Promise<void> {
  const response = await fetch(`${getAxiosPyrgosConfig().baseURL}${CLARA_STREAM_ENDPOINT}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify({ question: params.question }),
  });

  const isRefused = !response.ok;
  if (isRefused) {
    throw new ClaraStreamHttpError(response.status);
  }

  const reader = response.body?.getReader();
  const hasNoBody = isNil(reader);
  if (hasNoBody) {
    throw new ClaraStreamInterruptedError();
  }

  const isAnswerComplete = await readUntilDone(reader, params.onChunk);
  if (!isAnswerComplete) {
    throw new ClaraStreamInterruptedError();
  }
}
